import { dfApi as api } from '@services/df/baseApi';
const injectedRtkApi = api.injectEndpoints({
    endpoints: build => ({
        inventoryPurchaseTemplatesList: build.query<
            InventoryPurchaseTemplatesListApiResponse,
            InventoryPurchaseTemplatesListApiArg
        >({
            query: queryArg => ({
                url: `/v2/inventory/purchase-templates/`,
                params: { limit: queryArg.limit, offset: queryArg.offset, ordering: queryArg.ordering },
            }),
        }),
        inventoryPurchaseTemplatesRetrieve: build.query<
            InventoryPurchaseTemplatesRetrieveApiResponse,
            InventoryPurchaseTemplatesRetrieveApiArg
        >({
            query: queryArg => ({ url: `/v2/inventory/purchase-templates/${queryArg.id}/` }),
        }),
        inventoryPurchaseTemplatesCreateOrderCreate: build.mutation<
            InventoryPurchaseTemplatesCreateOrderCreateApiResponse,
            InventoryPurchaseTemplatesCreateOrderCreateApiArg
        >({
            query: queryArg => ({
                url: `/v2/inventory/purchase-templates/create-order/`,
                method: 'POST',
                body: queryArg.templatesIdsRequestRequest,
            }),
        }),
    }),
    overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type InventoryPurchaseTemplatesListApiResponse = /** status 200  */ PaginatedUserPurchaseTemplateList;
export type InventoryPurchaseTemplatesListApiArg = {
    /** Number of results to return per page. */
    limit?: number;
    /** The initial index from which to return the results. */
    offset?: number;
    /** Which field to use when ordering the results. */
    ordering?: string;
};
export type InventoryPurchaseTemplatesRetrieveApiResponse = /** status 200  */ UserPurchaseTemplate;
export type InventoryPurchaseTemplatesRetrieveApiArg = {
    id: number;
};
export type InventoryPurchaseTemplatesCreateOrderCreateApiResponse =
    /** status 200  */ InitialOrderResponseSerializerV2;
export type InventoryPurchaseTemplatesCreateOrderCreateApiArg = {
    templatesIdsRequestRequest: TemplatesIdsRequestRequest;
};
export type ConfigFormattedItem = {
    key: string;
    value: string;
};
export type UserPurchaseTemplate = {
    id: number;
    object_model: number;
    material: number;
    priority_days?: string | null;
    full_price: number;
    quantity?: number;
    config: any;
    object_model_title: string;
    material_title?: string;
    technology_title?: string;
    thumb: string;
    thumb_120x120: string;
    thumb_300x300: string | null;
    created_at: string;
    config_formatted_json: ConfigFormattedItem[];
};
export type PaginatedUserPurchaseTemplateList = {
    count: number;
    next?: string | null;
    previous?: string | null;
    results: UserPurchaseTemplate[];
};
export type CurrencyCodeEnum =
    | 'aed'
    | 'aud'
    | 'brl'
    | 'cad'
    | 'chf'
    | 'clp'
    | 'czk'
    | 'dkk'
    | 'eur'
    | 'gbp'
    | 'hkd'
    | 'huf'
    | 'idr'
    | 'ils'
    | 'inr'
    | 'krw'
    | 'mxn'
    | 'myr'
    | 'ngn'
    | 'nok'
    | 'nzd'
    | 'pln'
    | 'rsd'
    | 'rub'
    | 'sar'
    | 'sek'
    | 'sgd'
    | 'thb'
    | 'try'
    | 'twd'
    | 'uah'
    | 'usd'
    | 'zar';
export type NullEnum = null;
export type DiscountTypeEnum = 'promo' | 'client' | 'order' | 'batch' | 'hourly' | 'sq_cm_of_material';
export type DiscountCondition = {
    id: number;
    discount_value: number;
    condition_value?: number;
    code?: string;
};
export type WidgetResponseDiscount = {
    id: number;
    name_for_user?: string;
    description_for_user?: string;
    type: DiscountTypeEnum;
    conditions: DiscountCondition[];
};
export type TbdReasonEnum =
    | 'line_item_total'
    | 'order_total'
    | 'machine_quantity'
    | 'non_cad'
    | 'lead_time_quantity'
    | 'complexity_is_too_high'
    | 'no_suitable_feature'
    | 'purchase_attachment'
    | 'bends_quantity';
export type BlankEnum = '';
export type DrawingFile = {
    id: number;
    url: string;
    name: string;
};
export type BaseNotification = {
    code: string;
    text: string;
};
export type OrderStatusEnum =
    | 'created'
    | 'initial'
    | 'waiting_for_review'
    | 'firm_offer_sent'
    | 'placed'
    | 'po_provided'
    | 'waiting_for_payment'
    | 'on_hold'
    | 'free_for_manufacturing'
    | 'planning_confirmed'
    | 'manufacturing'
    | 'post_production'
    | 're_manufacturing'
    | 'ready_for_shipping'
    | 'shipped'
    | 'ready_for_pickup'
    | 'delivered'
    | 'po_payment_due'
    | 'cancelled_by_user'
    | 'cancelled_by_admin';
export type PurchaseResponseSerializerV2 = {
    id: number;
    /** Treat this ForeignKey as a extremely fragile and perishable. Material may be missing or changed since Purchase created */
    material_id: number | null;
    total_models_weight: number;
    manufacturing_hours?: number;
    tbd_reason?: (TbdReasonEnum | BlankEnum | NullEnum) | null;
    tbd_reason_message: string;
    count?: number;
    material_title: string;
    discount_percents: string;
    discount_value: number;
    applied_discount: WidgetResponseDiscount;
    naked_price: number;
    price_per_part: number;
    models_price: number;
    total_price: number;
    spec_string: string;
    config_formatted: string;
    config_formatted_json: ConfigFormattedItem[];
    material_display_name: string;
    model_title: string;
    model_id: number;
    parent_model_id: number;
    machine_id: number;
    machine_title: string;
    client_id: number;
    order_id: number | null;
    company_order_id: number;
    order_placed_at: string;
    order_paid_at: string;
    order_created_at: string;
    order_status: string;
    email: string;
    model_thumbnail: string;
    config: {
        [key: string]: any;
    };
    drawing_files: DrawingFile[];
    accept_drawing: boolean;
    require_drawing: boolean;
    priority_days: number;
    bbv: number | null;
    bbv_qnt: number | null;
    model_volume: number | null;
    model_volume_qnt: number | null;
    model_weight: number | null;
    model_weight_qnt: number | null;
    bb_weight: number | null;
    bb_weight_qnt: number | null;
    self_notes?: string | null;
    suggested_related_new_status?: string | null;
    sanity_notifications: BaseNotification[];
    status?: (OrderStatusEnum | NullEnum) | null;
    barstock_thickness?: number;
    barstock_size_x?: number;
    barstock_size_y?: number;
    barstock_bb_margin?: number;
    barstock_quantity?: number;
    from_template?: number | null;
    vendor?: number | null;
    vendor_email?: string | null;
};
export type InitialOrderResponseSerializerV2 = {
    id: number;
    currency?: CurrencyCodeEnum | NullEnum;
    locale?: string;
    upload_job?: string | null;
    use_additional_startup_cost?: boolean;
    delivery_time: number;
    models_naked_price: string;
    models_price: string;
    applied_discount: WidgetResponseDiscount;
    order_discount_in_percents: string;
    order_discount_value: string;
    price_corrections: string;
    price_min_difference: string;
    product_count: number;
    products: PurchaseResponseSerializerV2[];
    products_discount_value: string;
    startup_cost: string;
    delivery_price: string;
    tax_in_percents: number;
    total_discount_value: string;
    total_price: string;
    total_price_without_tax: string;
    available_order_initial_statuses: string[];
    tbd_reason?: (TbdReasonEnum | BlankEnum | NullEnum) | null;
    tbd_reason_message: string;
    buyer_id: number | null;
    easypost_rate?: string | null;
    custom_rate?: number | null;
    status: string;
    company_order_id: number;
    pdf_url: string | null;
    packing_slip_pdf_url: string | null;
    production_traveler_pdf_url: string | null;
    /** Comma-separated email addresses to which billing order emails will be sent */
    additional_billing_contacts?: string[];
    notes?: string;
};
export type TemplatesIdsRequestRequest = {
    /** List of templates IDs to create order with purchases from */
    template_ids: number[];
    order_id?: number;
};
export const {
    useInventoryPurchaseTemplatesListQuery,
    useInventoryPurchaseTemplatesRetrieveQuery,
    useInventoryPurchaseTemplatesCreateOrderCreateMutation,
} = injectedRtkApi;
